import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, LayoutGrid, Table2, Trash2, Mail, Phone, MapPin, Home, UserPlus, Link2, FileText, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { apiError, fileUrl } from "@/lib/api";
import { fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { SearchBox } from "@/components/SearchBox";

const STAGES = ["New", "Contacted", "Qualified", "Proposal Sent", "Won", "Lost"];
const STAGE_ACCENT = {
  New: "border-t-[#3a4a6b]", Contacted: "border-t-[#C6A87C]", Qualified: "border-t-[#8a6d3b]",
  "Proposal Sent": "border-t-[#D98324]", Won: "border-t-[#2B4C3F]", Lost: "border-t-[#9E382A]",
};
const LETTING_STATUS = ["New to holiday lets", "Already letting (self-managed)", "Already letting (via agent)"];

const empty = { name: "", email: "", phone: "", stage: "New", property_location: "", num_properties: "", property_type: "", currently_letting: "New to holiday lets", source: "", notes: "" };

function InvestorDialog({ trigger, initial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || empty);
  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial } : empty); }, [open]);

  const save = async () => {
    try {
      const payload = { ...form, num_properties: form.num_properties === "" ? null : Number(form.num_properties) };
      if (initial?.id) await api.put(`/investors/${initial.id}`, payload);
      else await api.post("/investors", payload);
      toast.success("Saved");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-head">{initial?.id ? "Edit Owner Lead" : "New Owner Lead"}</DialogTitle>
          <DialogDescription className="sr-only">A property owner registering interest in Bides managing their holiday let.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-y-auto thin-scroll pr-1">
          <div><Label className="overline">Name</Label><Input data-testid="investor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Email</Label><Input data-testid="investor-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="overline">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger data-testid="investor-stage-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Currently letting?</Label>
              <Select value={form.currently_letting} onValueChange={(v) => setForm({ ...form, currently_letting: v })}>
                <SelectTrigger data-testid="investor-letting-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{LETTING_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Property location</Label><Input data-testid="investor-location-input" value={form.property_location} onChange={(e) => setForm({ ...form, property_location: e.target.value })} className="mt-1.5" placeholder="Town / area" /></div>
            <div><Label className="overline"># of properties</Label><Input data-testid="investor-numprops-input" type="number" value={form.num_properties} onChange={(e) => setForm({ ...form, num_properties: e.target.value })} className="mt-1.5" placeholder="e.g. 1" /></div>
          </div>
          <div><Label className="overline">Property type</Label><Input data-testid="investor-type-input" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="mt-1.5" placeholder="e.g. 2-bed coastal cottage" /></div>
          <div><Label className="overline">Source</Label><Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1.5" placeholder="Referral, website, portal…" /></div>
          <div><Label className="overline">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" placeholder="What they're looking for from a managed let…" /></div>
        </div>
        <DialogFooter><Button data-testid="save-investor-button" onClick={save} disabled={!form.name}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SendProposalDialog({ inv, trigger, onSent }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [filename, setFilename] = useState("");
  const [message, setMessage] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const hasEmail = !!(inv.email || "").trim();
  const onFile = (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File too large (max 15MB)"); return; }
    setFilename(f.name);
    const r = new FileReader(); r.onload = () => setFile(r.result); r.readAsDataURL(f);
  };
  const send = async () => {
    if (!hasEmail) { toast.error("This lead has no email address"); return; }
    if (!file) { toast.error("Attach a document first"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/investors/${inv.id}/send-proposal`, { document: file, filename, message, note });
      toast.success(data.delivered ? `Proposal emailed to ${data.sent_to}` : `Proposal saved & link created (email not delivered)`);
      setOpen(false); setFile(null); setFilename(""); setMessage(""); setNote(""); onSent();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-head">Send proposal to {inv.name}</DialogTitle>
          <DialogDescription>{hasEmail ? `We'll email it to ${inv.email} with Accept / Decline options, and move this lead to "Proposal Sent".` : "This lead has no email — add one first."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label data-testid="proposal-file-label" className="flex items-center gap-2 text-sm border border-dashed border-border rounded-md px-3 py-4 cursor-pointer hover:bg-secondary/40">
            <Upload className="h-4 w-4 shrink-0" /><span className="truncate">{filename || "Choose document (PDF, image, up to 15MB)"}</span>
            <input type="file" accept=".pdf,image/*,.doc,.docx" className="hidden" data-testid="proposal-file-input" onChange={onFile} />
          </label>
          <div><Label className="overline">Message (optional)</Label><Textarea data-testid="proposal-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1.5" placeholder="A short note to accompany the proposal…" /></div>
          {(inv.proposal_history || []).length > 0 && (
            <div><Label className="overline">Why are you resending? (optional)</Label><Input data-testid="proposal-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder="e.g. Updated pricing / corrected address" /><p className="text-[0.65rem] text-muted-foreground mt-1">Kept as an internal note against this version in the history log.</p></div>
          )}
        </div>
        <DialogFooter><Button data-testid="send-proposal-button" onClick={send} disabled={sending || !file || !hasEmail}>{sending ? "Sending…" : "Send proposal"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Investors() {
  const [items, setItems] = useState([]);
  const [view, setView] = useState("board");
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const load = () => api.get("/investors", { params: { q } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [q]);

  const move = async (inv, stage) => {
    try { await api.put(`/investors/${inv.id}`, { stage }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (inv) => {
    try { await api.delete(`/investors/${inv.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const convert = async (inv) => {
    if (!window.confirm(`Convert ${inv.name} into an Owner record?`)) return;
    const draft = window.confirm("Also create a draft property to start onboarding?");
    let sendOnboarding = false;
    if (draft && (inv.email || "").trim()) {
      sendOnboarding = window.confirm(`Also email ${inv.email} the onboarding form now?`);
    }
    try {
      const { data } = await api.post(`/investors/${inv.id}/convert?create_property=${draft}&send_onboarding=${sendOnboarding}`);
      toast.success(data.onboarding_sent ? "Owner + draft property created · onboarding emailed" : (draft && data.property_id ? "Owner + draft property created" : "Converted to Owner"));
      nav(data.property_id ? `/properties/${data.property_id}` : `/landlords/${data.landlord_id}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const copyLink = async () => {
    const url = `${window.location.origin}/owner-interest`;
    try { await navigator.clipboard.writeText(url); toast.success("Public signup link copied"); }
    catch { toast.error(url); }
  };
  const propLine = (inv) => [inv.property_type, inv.property_location].filter(Boolean).join(" · ");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="overline text-accent">Pipeline</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight mt-1">Owner Leads</h1>
          <p className="text-sm text-muted-foreground mt-1">Property owners registering interest in Bides operating their managed holiday let.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox testId="owner-leads-search" placeholder="Search name, email, phone, area…" onSearch={setQ} />
          <Button variant="outline" data-testid="owner-signup-link-btn" onClick={copyLink}><Link2 className="h-4 w-4 mr-1" />Public signup link</Button>
          <div className="flex border border-border rounded-md overflow-hidden">
            <button data-testid="inv-board-btn" onClick={() => setView("board")} className={`px-3 py-2 flex items-center gap-1.5 text-sm ${view === "board" ? "bg-[#18181A] text-white" : "bg-white"}`}><LayoutGrid className="h-4 w-4" /> Board</button>
            <button data-testid="inv-table-btn" onClick={() => setView("table")} className={`px-3 py-2 flex items-center gap-1.5 text-sm ${view === "table" ? "bg-[#18181A] text-white" : "bg-white"}`}><Table2 className="h-4 w-4" /> Table</button>
          </div>
          <InvestorDialog trigger={<Button data-testid="new-investor-button"><Plus className="h-4 w-4 mr-1" />New Owner Lead</Button>} onSaved={load} />
        </div>
      </div>

      {q && items.length === 0 && <p data-testid="owner-leads-empty-search" className="text-sm text-muted-foreground">No owner leads match "{q}".</p>}
      {view === "board" ? (
        <div className="flex gap-4 overflow-x-auto thin-scroll pb-3">
          {STAGES.map((st) => {
            const col = items.filter((i) => i.stage === st);
            return (
              <div key={st} className={`bg-secondary/40 border border-border border-t-2 ${STAGE_ACCENT[st]} rounded-md p-3 min-w-[240px] w-[240px] shrink-0`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="overline text-[0.6rem]">{st}</span>
                  <span className="text-xs font-mono text-muted-foreground">{col.length}</span>
                </div>
                <div className="space-y-2">
                  {col.map((inv) => (
                    <motion.div layout key={inv.id} data-testid={`investor-card-${inv.id}`} className="bg-white border border-border rounded-md p-3">
                      <p className="text-sm font-semibold">{inv.name}</p>
                      {propLine(inv) && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Home className="h-3 w-3" />{propLine(inv)}</p>}
                      {inv.currently_letting && <p className="text-[0.65rem] text-[#8a6d3b] mt-1">{inv.currently_letting}{inv.num_properties ? ` · ${inv.num_properties} property(ies)` : ""}</p>}
                      {inv.converted_landlord_id && <p className="text-[0.6rem] mt-1 inline-block px-2 py-0.5 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F]">✓ Owner created</p>}
                      {inv.proposal_status === "sent" && <p className="text-[0.6rem] mt-1 inline-block px-2 py-0.5 rounded-full bg-[#D98324]/15 text-[#D98324]">{inv.proposal_viewed_at ? "Proposal viewed · awaiting reply" : "Proposal sent · awaiting reply"}</p>}
                      {inv.proposal_status === "accepted" && <p className="text-[0.6rem] mt-1 inline-block px-2 py-0.5 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F]">✓ Accepted{inv.proposal_signed_name ? ` by ${inv.proposal_signed_name}` : ""}</p>}
                      {inv.proposal_status === "accepted" && inv.proposal_signature && <a href={fileUrl(inv.proposal_signature)} target="_blank" rel="noreferrer" data-testid={`inv-signature-${inv.id}`} className="text-[0.6rem] underline text-muted-foreground block mt-0.5">View signature</a>}
                      {inv.proposal_status === "declined" && <p className="text-[0.6rem] mt-1 inline-block px-2 py-0.5 rounded-full bg-[#9E382A]/12 text-[#9E382A]">Proposal declined</p>}
                      {(inv.proposal_history || []).length > 0 && (
                        <details className="mt-1" data-testid={`inv-history-${inv.id}`}>
                          <summary className="text-[0.6rem] text-muted-foreground cursor-pointer select-none">Proposal history ({inv.proposal_history.length})</summary>
                          <div className="mt-1 space-y-1">
                            {inv.proposal_history.map((h, i) => ({ ...h, _v: i + 1 })).reverse().map((h) => (
                              <div key={h.id} className="text-[0.6rem] text-muted-foreground border-l-2 border-border pl-2 flex items-center gap-1.5 flex-wrap">
                                <span className="font-medium text-foreground">v{h._v}</span>
                                <span>{fmtDate(h.sent_at)} · <span className="capitalize">{h.status}</span>{h.viewed_at ? " · viewed" : ""}{h.signed_name ? ` · ${h.signed_name}` : ""}</span>
                                {h.doc && <a href={fileUrl(h.doc)} target="_blank" rel="noreferrer" data-testid={`inv-history-doc-${inv.id}-v${h._v}`} className="underline text-[#D98324] inline-flex items-center gap-0.5"><FileText className="h-2.5 w-2.5" />{h.filename || "document"}</a>}
                                {h.note && <span className="w-full italic text-muted-foreground/80" data-testid={`inv-history-note-${inv.id}-v${h._v}`}>"{h.note}"</span>}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                      <div className="flex items-center gap-1 mt-2">
                        <Select value={inv.stage} onValueChange={(v) => move(inv, v)}>
                          <SelectTrigger data-testid={`inv-stage-${inv.id}`} className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                        {(inv.stage === "Qualified" || inv.stage === "Proposal Sent") && !inv.converted_landlord_id && (
                          <SendProposalDialog inv={inv} onSent={load} trigger={<Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`inv-proposal-${inv.id}`} title={inv.proposal_status === "sent" ? "Resend proposal" : "Send proposal"}><FileText className="h-3.5 w-3.5 text-[#D98324]" /></Button>} />
                        )}
                        {!inv.converted_landlord_id && <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`inv-convert-${inv.id}`} title="Convert to Owner" onClick={() => convert(inv)}><UserPlus className="h-3.5 w-3.5 text-[#2B4C3F]" /></Button>}
                        <InvestorDialog initial={inv} trigger={<Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`inv-edit-${inv.id}`}><Mail className="h-3.5 w-3.5" /></Button>} onSaved={load} />
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`inv-delete-${inv.id}`} onClick={() => remove(inv)}><Trash2 className="h-3.5 w-3.5 text-[#9E382A]" /></Button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-secondary/40 text-left">
              <th className="px-4 py-3 overline font-semibold">Name</th>
              <th className="px-4 py-3 overline font-semibold">Contact</th>
              <th className="px-4 py-3 overline font-semibold">Property</th>
              <th className="px-4 py-3 overline font-semibold">Stage</th>
              <th className="px-4 py-3"></th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {items.map((inv) => (
                <tr key={inv.id} data-testid={`investor-row-${inv.id}`}>
                  <td className="px-4 py-3 font-medium">{inv.name}<p className="text-xs text-muted-foreground font-normal">{inv.currently_letting}</p></td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">
                    {inv.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{inv.email}</span>}
                    {inv.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{inv.phone}</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground"><span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{propLine(inv) || "—"}</span></td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded-full bg-secondary">{inv.stage}</span>{inv.proposal_status && inv.proposal_status !== "" && <span className={`ml-1 text-[0.6rem] px-2 py-0.5 rounded-full ${inv.proposal_status === "accepted" ? "bg-[#2B4C3F]/12 text-[#2B4C3F]" : inv.proposal_status === "declined" ? "bg-[#9E382A]/12 text-[#9E382A]" : "bg-[#D98324]/15 text-[#D98324]"}`}>{inv.proposal_status}</span>}</td>
                  <td className="px-4 py-3 text-right">
                    {(inv.stage === "Qualified" || inv.stage === "Proposal Sent") && !inv.converted_landlord_id && (
                      <SendProposalDialog inv={inv} onSent={load} trigger={<Button size="sm" variant="ghost" data-testid={`inv-proposal-row-${inv.id}`}><FileText className="h-4 w-4 mr-1 text-[#D98324]" />Proposal</Button>} />
                    )}
                    {inv.converted_landlord_id
                      ? <span className="text-[0.65rem] px-2 py-1 rounded-full bg-[#2B4C3F]/12 text-[#2B4C3F] mr-1">✓ Owner</span>
                      : <Button size="sm" variant="ghost" data-testid={`inv-convert-row-${inv.id}`} onClick={() => convert(inv)}><UserPlus className="h-4 w-4 mr-1 text-[#2B4C3F]" />Convert</Button>}
                    <InvestorDialog initial={inv} trigger={<Button size="sm" variant="ghost">Edit</Button>} onSaved={load} />
                    <Button size="icon" variant="ghost" onClick={() => remove(inv)}><Trash2 className="h-4 w-4 text-[#9E382A]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="p-10 text-center text-sm text-muted-foreground">No owner leads yet.</p>}
        </div>
      )}
    </div>
  );
}
